/**
 * PrioritySection Component
 *
 * Card displaying priority distribution with compact rows for each priority level.
 *
 * @module pages/DashboardPage/components/PrioritySection
 */

import { memo } from 'react';
import { Skeleton } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { IconClipboardList, IconFlag } from '@tabler/icons-react';

import { ICON_SIZE_SM } from '@/constants/ui';
import { EmptyState } from '@/components/common';

import { PriorityRow } from '../PriorityRow';
import type { PrioritySectionProps } from './PrioritySection.types';
import styles from './PrioritySection.module.css';

const SKELETON_ROW_COUNT = 4;

/**
 * PrioritySection renders a card with compact priority distribution rows.
 */
export const PrioritySection = memo(function PrioritySection({
  priorities,
  totalTasks,
  className,
  isLoading,
}: PrioritySectionProps) {
  const { t } = useTranslation();

  const showSkeleton = isLoading && priorities.length === 0;

  return (
    <section className={clsx(styles.section, className)}>
      <div className={styles.header}>
        <IconFlag size={ICON_SIZE_SM} />
        <span>{t('dashboard.priorities.title')}</span>
      </div>
      {showSkeleton ? (
        <div>
          {Array.from({ length: SKELETON_ROW_COUNT }, (_, idx) => (
            <div key={idx} className={styles.skeletonRow}>
              <Skeleton height={32} width={32} radius="md" />
              <div className={styles.skeletonInfo}>
                <Skeleton height={14} width="40%" radius="sm" />
                <Skeleton height={8} width="100%" radius="md" />
              </div>
              <Skeleton height={20} width={40} radius="sm" />
            </div>
          ))}
        </div>
      ) : priorities.length === 0 ? (
        <EmptyState
          size="sm"
          title={t('dashboard.emptyState.priorities')}
          icon={<IconClipboardList size={32} />}
        />
      ) : (
        <div>
          {priorities.map((priorityData) => (
            <PriorityRow
              key={priorityData.priority}
              data={priorityData}
              totalTasks={totalTasks}
              compact
            />
          ))}
        </div>
      )}
    </section>
  );
});

PrioritySection.displayName = 'PrioritySection';
